import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Send, Sparkles, Trash2, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatbotEmbed = () => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const chatContainerRef = useRef(null);
  const hasUserInteracted = useRef(false);

  const scrollToBottom = () => {
    // Only scroll within the chat container, not the whole page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Only scroll if user has sent a message (not on initial load or language change)
    if (hasUserInteracted.current) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    // Welcome message based on language - only set if no messages
    if (messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: t('chatbot.welcome')
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update welcome message when language changes
  useEffect(() => {
    setMessages(prevMessages => {
      if (prevMessages.length === 1 && prevMessages[0].id === 'welcome') {
        return [{
          id: 'welcome',
          role: 'assistant',
          content: t('chatbot.welcome')
        }];
      }
      return prevMessages;
    });
  }, [i18n.language, t]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // Mark that user has interacted - now scrolling is allowed
    hasUserInteracted.current = true;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage.content
      });

      const assistantMessage = {
        id: Date.now().toString() + '_assistant',
        role: 'assistant',
        content: response.data.response
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now().toString() + '_error',
        role: 'assistant',
        content: t('chatbot.error')
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/${sessionId}`);
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: t('chatbot.welcome')
    }]);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-stone-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-navy/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-white/60 backdrop-blur-md border border-gold/20 shadow-lg shadow-gold/5">
            <MessageCircle className="w-4 h-4 text-gold" />
            <span className="text-xs font-medium text-navy/70 tracking-wider uppercase">
              {t('chatbot.title')}
            </span>
          </div>
        </motion.div>

        {/* Chat Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 100%)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)'
          }}
        >
          {/* Chat Header */}
          <div 
            className="px-6 py-4 flex items-center justify-between"
            style={{
              background: 'linear-gradient(135deg, rgba(26,54,93,0.95) 0%, rgba(26,54,93,0.85) 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_glass-portfolio-27/artifacts/2rj2w1yt_logo_small.png" 
                  alt="Limitless Solutions" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm">{t('chatbot.title')}</h3>
                <p className="text-white/60 text-xs">{t('chatbot.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={clearChat}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title={t('chatbot.clearChat')}
            >
              <Trash2 className="w-4 h-4 text-white/60" />
            </button>
          </div>

          {/* Messages */}
          <div ref={chatContainerRef} className="h-[350px] overflow-y-auto p-6 space-y-4">
            {messages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-navy text-white rounded-br-md'
                      : 'rounded-bl-md'
                  }`}
                  style={msg.role === 'assistant' ? {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    border: '1px solid rgba(201,169,98,0.2)',
                    color: '#1a365d'
                  } : {}}
                >
                  <p className="text-sm font-light whitespace-pre-wrap leading-relaxed">
                    {msg.content}
                  </p>
                </div>
              </motion.div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div 
                  className="px-4 py-3 rounded-2xl rounded-bl-md"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    border: '1px solid rgba(201,169,98,0.2)'
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <motion.div
                      className="w-2 h-2 bg-gold rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div
                      className="w-2 h-2 bg-gold rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                    />
                    <motion.div
                      className="w-2 h-2 bg-gold rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gold/10">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chatbot.placeholder')}
                disabled={isLoading}
                data-testid="chatbot-input"
                className="flex-1 px-4 py-3 rounded-xl text-sm text-navy font-light placeholder:text-navy/40 focus:outline-none disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 100%)',
                  border: '1px solid rgba(201,169,98,0.2)'
                }}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                data-testid="chatbot-send-button"
                className="p-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, rgba(26,54,93,0.95) 0%, rgba(26,54,93,0.85) 100%)',
                  boxShadow: '0 4px 12px rgba(26,54,93,0.3)'
                }}
              >
                <Send className="w-5 h-5 text-white" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ChatbotEmbed;
